import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import type { JobState } from '../api/types'

interface IterationResponse {
  id: string
  proposed_queries: string[] | null
  executed_queries: string[] | null
}

function QueryReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [queries, setQueries] = useState<string[]>([])
  const [newQuery, setNewQuery] = useState('')
  const [status, setStatus] = useState<JobState | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    if (!id) return
    try {
      const runRes = await apiClient.get<{ status: JobState }>(`/research/runs/${id}`)
      setStatus((runRes.data as { status: JobState }).status)
      const itRes = await apiClient.get<IterationResponse[]>(`/research/runs/${id}/iterations`)
      const iterations = itRes.data as unknown as IterationResponse[]
      const last = iterations[iterations.length - 1]
      const proposed = last?.proposed_queries ?? last?.executed_queries ?? []
      setQueries(proposed)
      setError(null)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Failed to load queries')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const handleEdit = (idx: number, newText: string) => {
    setQueries((prev) => prev.map((q, i) => (i === idx ? newText : q)))
  }

  const handleDelete = (idx: number) => {
    setQueries((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleAdd = () => {
    const trimmed = newQuery.trim()
    if (!trimmed) return
    setQueries((prev) => [...prev, trimmed])
    setNewQuery('')
  }

  const handleConfirm = async () => {
    if (!id) return
    const cleaned = queries.map((q) => q.trim()).filter(Boolean)
    if (cleaned.length === 0) {
      setError('At least one query is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await apiClient.patch(`/research/runs/${id}/queries`, { queries: cleaned })
      await apiClient.post(`/research/runs/${id}/confirm`)
      navigate(`/runs/${id}`)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Confirm failed')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async () => {
    if (!id) return
    if (!confirm('Cancel this run? Already-added documents will remain.')) return
    try {
      await apiClient.post(`/research/runs/${id}/cancel`)
      navigate('/dashboard')
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Cancel failed')
    }
  }

  if (loading) return <div className="page-header"><h1 className="page-title">Loading queries...</h1></div>

  if (status && status !== 'waiting_for_user') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Review Queries — Run {id?.slice(0, 8)}</h1>
          <Link to={`/runs/${id}`} className="page-action-secondary">Back to Run</Link>
        </div>
        <div className="review-card">
          <p>Run is not waiting for review (status: <strong>{status}</strong>).</p>
          <Link to={`/runs/${id}`} className="button-primary">Go to Run</Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Review Queries — Run {id?.slice(0, 8)}</h1>
        <Link to={`/runs/${id}`} className="page-action-secondary">Back to Run</Link>
      </div>

      <div className="review-card">
        <p className="review-description">Review, edit, delete, or add search queries before the research run continues.</p>

        {error && <p style={{ color: '#c00', fontSize: 14 }}>{error}</p>}

        <ul className="query-list">
          {queries.map((q, idx) => (
            <li key={idx} className="query-item">
              <input type="text" className="query-input" value={q} onChange={(e) => handleEdit(idx, e.target.value)} />
              <button className="query-delete" onClick={() => handleDelete(idx)}>Delete</button>
            </li>
          ))}
          {queries.length === 0 && <li style={{ fontSize: 14, color: '#666' }}>No queries yet — add one below.</li>}
        </ul>

        <div className="query-add-row">
          <input
            type="text"
            className="query-input"
            placeholder="Add a new query..."
            value={newQuery}
            onChange={(e) => setNewQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAdd()
              }
            }}
          />
          <button className="query-add" onClick={handleAdd}>Add</button>
        </div>

        <div className="review-actions">
          <button className="button-primary" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Saving...' : 'Confirm & Continue'}
          </button>
          <button className="button-danger" onClick={handleCancel}>Cancel Run</button>
        </div>
      </div>
    </div>
  )
}

export default QueryReviewPage
